import React from "react";
import { FeatherRefreshCw, FeatherSearch } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { Loader } from "../../../../ui/components/Loader";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import { formatDate, truncateText } from "../../../../shared/utils/formatters";
import { DocumentEntity } from "../../domain/entities/Document";
import { DocumentTypeFilterPills } from "./DocumentTypeFilterPills";
import { DocumentCategory } from "./documentFilters";
import { PaginationControls } from "./PaginationControls";
import {
  canRetryReducto,
  getReductoRowState,
  REDUCTO_STATE_LABEL,
} from "./reductoStatementState";

interface DocumentListSectionProps {
  title: string;
  variant: "pending" | "processed";
  documents: DocumentEntity[];
  loading: boolean;
  tableLoading?: boolean;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  selectedCategory: DocumentCategory;
  onSelectedCategoryChange: (value: DocumentCategory) => void;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasMore: boolean;
  };
  itemsPerPage: number;
  retryingJobId: string | null;
  renderTypeBadge: (doc: DocumentEntity) => React.ReactNode;
  renderStatusBadge: (doc: DocumentEntity) => React.ReactNode;
  shouldShowRetryButton: (doc: DocumentEntity) => boolean;
  onRefresh: () => void;
  onPageChange: (page: number) => void;
  onDocumentClick: (doc: DocumentEntity) => void;
  onRetry: (doc: DocumentEntity) => void;
  onViewReport: (doc: DocumentEntity) => void;
  /** Opens the review-before-push dialog for statements awaiting approval */
  onReviewStatement?: (doc: DocumentEntity) => void;
  /** Manual re-run of a failed/stuck Reducto extraction */
  onRetryReducto?: (doc: DocumentEntity) => void;
  /** Explicit hand-off of a rejected statement to the legacy Textract flow */
  onTextractStatement?: (doc: DocumentEntity) => void;
  /** statement_preprocessing id currently being retried */
  retryingStatementId?: string | null;
}

export const DocumentListSection: React.FC<DocumentListSectionProps> = ({
  title,
  variant,
  documents,
  loading,
  tableLoading = loading,
  searchTerm,
  onSearchTermChange,
  selectedCategory,
  onSelectedCategoryChange,
  pagination,
  itemsPerPage,
  retryingJobId,
  renderTypeBadge,
  renderStatusBadge,
  shouldShowRetryButton,
  onRefresh,
  onPageChange,
  onDocumentClick,
  onRetry,
  onViewReport,
  onReviewStatement,
  onRetryReducto,
  onTextractStatement,
  retryingStatementId,
}) => {
  const isPending = variant === "pending";

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full items-center justify-between">
        <span className="text-heading-3 font-heading-3 text-default-font">
          {title}
        </span>
      </div>

      <div className="flex w-full items-center justify-between gap-4">
        <DocumentTypeFilterPills
          value={selectedCategory}
          onChange={onSelectedCategoryChange}
        />

        <div className="flex items-center gap-2">
          <TextField
            className="h-auto w-80 flex-none"
            label=""
            helpText=""
            icon={<FeatherSearch />}
          >
            <TextField.Input
              placeholder={`Search ${title.toLowerCase()} documents...`}
              value={searchTerm}
              onChange={(event) => onSearchTermChange(event.target.value)}
            />
          </TextField>
          <Button
            variant="neutral-secondary"
            icon={<FeatherRefreshCw />}
            onClick={onRefresh}
            disabled={loading}
            loading={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="w-full min-h-48 max-h-96 flex flex-col">
        <div className="flex flex-col flex-1 min-h-0">
          <div className="overflow-auto flex-1 min-h-0">
            <Table
              className="w-full"
              style={{ tableLayout: "fixed" }}
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell
                    style={{ width: "240px", paddingRight: "16px" }}
                  >
                    Document Name
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ width: "140px", paddingRight: "16px" }}
                  >
                    Type
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ width: "220px", paddingRight: "16px" }}
                  >
                    Upload Date
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ width: "480px", paddingRight: "16px" }}
                  >
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: "180px" }}>
                    {isPending ? "Stage Completed" : "Actions"}
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {tableLoading ? (
                <TableSkeletonRows rowCount={5} columnCount={5} />
              ) : loading ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <div className="flex w-full items-center justify-center py-8">
                      <Loader />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : (
                renderRows({
                  documents,
                  isPending,
                  retryingJobId,
                  renderTypeBadge,
                  renderStatusBadge,
                  shouldShowRetryButton,
                  onDocumentClick,
                  onRetry,
                  onViewReport,
                  onReviewStatement,
                  onRetryReducto,
                  onTextractStatement,
                  retryingStatementId,
                })
              )}
            </Table>
          </div>

          {!tableLoading && !loading && (
            <PaginationControls
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              hasMore={pagination.hasMore}
              itemsPerPage={itemsPerPage}
              onPageChange={onPageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface RenderRowsOptions {
  documents: DocumentEntity[];
  isPending: boolean;
  retryingJobId: string | null;
  renderTypeBadge: (doc: DocumentEntity) => React.ReactNode;
  renderStatusBadge: (doc: DocumentEntity) => React.ReactNode;
  shouldShowRetryButton: (doc: DocumentEntity) => boolean;
  onDocumentClick: (doc: DocumentEntity) => void;
  onRetry: (doc: DocumentEntity) => void;
  onViewReport: (doc: DocumentEntity) => void;
  onReviewStatement?: (doc: DocumentEntity) => void;
  onRetryReducto?: (doc: DocumentEntity) => void;
  onTextractStatement?: (doc: DocumentEntity) => void;
  retryingStatementId?: string | null;
}

const renderRows = ({
  documents,
  isPending,
  retryingJobId,
  renderTypeBadge,
  renderStatusBadge,
  shouldShowRetryButton,
  onDocumentClick,
  onRetry,
  onViewReport,
  onReviewStatement,
  onRetryReducto,
  onTextractStatement,
  retryingStatementId,
}: RenderRowsOptions) => {
  if (documents.length === 0) {
    return (
      <Table.Row>
        <Table.Cell colSpan={5}>
          <div className="flex w-full items-center justify-center py-8">
            <span className="text-body font-body text-neutral-500">
              No {isPending ? "pending" : "processed"} documents found
            </span>
          </div>
        </Table.Cell>
      </Table.Row>
    );
  }

  return documents.map((doc) => {
    const isReviewableNote =
      doc.documentType === "credit_note" || doc.documentType === "debit_note";

    return (
      <Table.Row
        key={`${doc.type}-${doc.id}`}
        onClick={() => {
          if (isReviewableNote) onDocumentClick(doc);
        }}
        clickable={isReviewableNote}
        className={isReviewableNote ? "cursor-pointer" : ""}
      >
        <Table.Cell style={{ width: "240px", paddingRight: "16px" }}>
          <span className="text-body-bold font-body-bold text-default-font truncate block">
            {truncateText(doc.originalFilename || doc.filename || "", 35)}
          </span>
        </Table.Cell>
        <Table.Cell style={{ width: "140px", paddingRight: "16px" }}>
          {renderTypeBadge(doc)}
        </Table.Cell>
        <Table.Cell style={{ width: "220px", paddingRight: "16px" }}>
          <span className="text-body font-body text-neutral-500 whitespace-nowrap block">
            {formatDate(doc.createdAt, "long")}
          </span>
        </Table.Cell>
        <Table.Cell style={{ width: "480px", paddingRight: "16px" }}>
          <div className="flex flex-col items-start gap-1 min-w-0">
            {renderStatusBadge(doc)}
            <span className="text-caption font-caption text-subtext-color break-words">
              Uploaded {formatDate(doc.createdAt, "long")}
            </span>
          </div>
        </Table.Cell>
        <Table.Cell style={{ width: "180px" }}>
          {isPending ? (
            <PendingDocumentActions
              doc={doc}
              retryingJobId={retryingJobId}
              shouldShowRetryButton={shouldShowRetryButton}
              onRetry={onRetry}
              onReviewStatement={onReviewStatement}
              onRetryReducto={onRetryReducto}
              onTextractStatement={onTextractStatement}
              retryingStatementId={retryingStatementId}
            />
          ) : (
            <ProcessedDocumentActions doc={doc} onViewReport={onViewReport} />
          )}
        </Table.Cell>
      </Table.Row>
    );
  });
};

interface PendingDocumentActionsProps {
  doc: DocumentEntity;
  retryingJobId: string | null;
  shouldShowRetryButton: (doc: DocumentEntity) => boolean;
  onRetry: (doc: DocumentEntity) => void;
  onReviewStatement?: (doc: DocumentEntity) => void;
  onRetryReducto?: (doc: DocumentEntity) => void;
  onTextractStatement?: (doc: DocumentEntity) => void;
  retryingStatementId?: string | null;
}

const PendingDocumentActions: React.FC<PendingDocumentActionsProps> = ({
  doc,
  retryingJobId,
  shouldShowRetryButton,
  onRetry,
  onReviewStatement,
  onRetryReducto,
  onTextractStatement,
  retryingStatementId,
}) => {
  // Reducto statements report their own extraction state; everything else
  // keeps showing the pipeline's stage_completed text.
  const reductoState = getReductoRowState(doc);
  const isRetryingThis = retryingStatementId === doc.id;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span
        className={`text-body font-body truncate ${
          reductoState === "failed" ? "text-error-700" : "text-neutral-500"
        }`}
        title={
          reductoState === "failed"
            ? doc.jobErrorMessage || "Failed"
            : undefined
        }
      >
        {reductoState !== "none"
          ? REDUCTO_STATE_LABEL[reductoState]
          : truncateText(doc.stageCompleted || "", 25)}
      </span>
      {doc.type === "statement" &&
        doc.status === "awaiting_approval" &&
        onReviewStatement && (
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onReviewStatement(doc);
            }}
          >
            Review
          </Button>
        )}
      {canRetryReducto(reductoState) && onRetryReducto && (
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onRetryReducto(doc);
          }}
          disabled={isRetryingThis}
          loading={isRetryingThis}
          icon={<FeatherRefreshCw />}
        >
          Retry
        </Button>
      )}
      {/* Rejected is terminal — no follow-up action, by user decision. The
          POST review/{id}/textract hand-off endpoint still exists backend-side
          but is deliberately not exposed anywhere in the UI. */}
      {reductoState === "none" && shouldShowRetryButton(doc) && (
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onRetry(doc);
          }}
          disabled={retryingJobId === doc.jobId}
          loading={retryingJobId === doc.jobId}
          icon={<FeatherRefreshCw />}
        >
          Retry
        </Button>
      )}
    </div>
  );
};

interface ProcessedDocumentActionsProps {
  doc: DocumentEntity;
  onViewReport: (doc: DocumentEntity) => void;
}

const ProcessedDocumentActions: React.FC<ProcessedDocumentActionsProps> = ({
  doc,
  onViewReport,
}) => {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {doc.type === "statement" && doc.transactionsJson && (
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onViewReport(doc);
          }}
        >
          View Report
        </Button>
      )}
    </div>
  );
};
